import { useEffect, useRef, useState } from 'react';
import Live2DCanvas from '../components/Live2D/Live2DCanvas';
import MascotBubble from '../components/MascotBubble/MascotBubble';
import MascotPanel from '../components/MascotPanel/MascotPanel';
import ChoiceBox from '../components/ChoiceBox/ChoiceBox';
import { useMessageSystem } from '../hooks/useMessageSystem';
import { useChoice } from '../hooks/useChoice';
import { usePomodoroTimer, showRestChoiceIfNeeded } from '../hooks/usePomodoroTimer';
import { useBgm } from '../hooks/useBgm';
import { usePomodoroStore } from '../stores/useAppStore';
import { useDressStore } from '../stores/useDressStore';
import { useSettingsStore } from '../stores/useSettingsStore';
import { useTranslation } from '../i18n/LanguageContext';
import { dressList, dressNames } from '../data/dressList';
import { bgmList } from '../data/bgmList';
import { pickMascotTalk } from '../data/mascotTalk';
import type { Live2DModel } from 'pixi-live2d-display/cubism4';
import './MascotPage.css';

// Electronの透過ウィンドウに空ちゃんだけを表示するデスクトップマスコットモード
const MascotPage = () => {
  const { selectedDress, dressUnlocks } = useDressStore();
  const { mascotPanelSide } = useSettingsStore();
  const { language } = useTranslation();
  const live2dModelRef = useRef<Live2DModel | null>(null);
  const greetedRef = useRef(false);
  const [panelOpen, setPanelOpen] = useState(false);

  // パネルの左右切り替え時、空ちゃんの画面上の位置が変わらないよう
  // ウィンドウを平行移動する（初回マウント時は動かさない）
  const prevSideRef = useRef(mascotPanelSide);
  useEffect(() => {
    if (prevSideRef.current === mascotPanelSide) return;
    prevSideRef.current = mascotPanelSide;
    // 左→右: ステージがウィンドウ左端に移るためウィンドウを右へ300pxずらす
    window.mascotApi?.shiftWindow(mascotPanelSide === 'right' ? 300 : -300);
  }, [mascotPanelSide]);

  const { currentMessage, messageVisible, showMessageWithMouth } =
    useMessageSystem(live2dModelRef);

  // 選択肢システム（心配ライン超過時の休息うながし等）
  const {
    isChoiceVisible,
    currentScenario,
    showChoice,
    handleSelect,
    handleTimeout,
  } = useChoice();

  // ポモドーロタイマー（完了・マイルストーンのメッセージは吹き出しに流れる）
  const {
    formattedTime,
    isRunning,
    phase,
    handleStart,
    handleStop,
    handleReset,
  } = usePomodoroTimer(showMessageWithMouth, showChoice);

  // BGM（パネルを閉じても再生が止まらないよう、フックはページ側で保持）
  const {
    isBgmPlaying,
    currentBgmIndex,
    bgmList: currentBgmList,
    playBgm,
    stopBgm,
  } = useBgm(bgmList);

  // レコード盤クリック用: 最後に再生した曲を覚えておく
  const lastBgmIndexRef = useRef<number | null>(null);
  useEffect(() => {
    if (currentBgmIndex !== null) {
      lastBgmIndexRef.current = currentBgmIndex;
    }
  }, [currentBgmIndex]);

  const handleRecordClick = () => {
    if (isBgmPlaying) {
      stopBgm();
      return;
    }
    const index =
      lastBgmIndexRef.current ??
      currentBgmList.findIndex((track) => track.unlocked);
    if (index >= 0) playBgm(index);
  };

  // 透過ウィンドウ用にhtml/body/#rootの背景を透明にする
  // （index.cssでhtmlとbodyの両方に黒背景が指定されているため両方に付ける）
  useEffect(() => {
    document.documentElement.classList.add('mascot-mode');
    document.body.classList.add('mascot-mode');
    return () => {
      document.documentElement.classList.remove('mascot-mode');
      document.body.classList.remove('mascot-mode');
    };
  }, []);

  // クリック素通し制御:
  // ウィンドウは常時 setIgnoreMouseEvents(true, {forward: true}) で素通し状態。
  // forward指定によりmousemoveだけはページに届くので、カーソルがキャラ（Live2Dモデルの矩形）
  // またはUI要素（吹き出し・パネル・各ボタン）の上にある間だけ受付に切り替える
  useEffect(() => {
    const api = window.mascotApi;
    if (!api) return; // ブラウザで/mascotを開いた場合は何もしない

    let lastInteractive: boolean | null = null;

    const isInteractivePoint = (x: number, y: number) => {
      // 体をつかんでウィンドウ移動している間は、カーソルが一瞬キャラの外に
      // 出ても素通しに切り替えない（切り替えるとドラッグが途切れる）
      if (document.body.dataset.mascotWindowDrag) {
        return true;
      }
      const el = document.elementFromPoint(x, y);
      if (
        el?.closest('.mascot-bubble, .mascot-pills, .mascot-panel, .choice-box-overlay')
      ) {
        return true;
      }
      // キャンバスはステージ全面なので、モデルの表示範囲で判定する
      const bounds = live2dModelRef.current?.getBounds();
      if (!bounds) return false;
      // キャンバス（ステージ）はウィンドウ右端に固定されているため座標を補正する
      const stage = document.querySelector('.mascot-stage');
      const offsetX = stage ? stage.getBoundingClientRect().left : 0;
      return bounds.contains(x - offsetX, y);
    };

    const onMouseMove = (e: MouseEvent) => {
      const interactive = isInteractivePoint(e.clientX, e.clientY);
      if (interactive !== lastInteractive) {
        lastInteractive = interactive;
        api.setIgnoreMouse(!interactive);
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, []);

  // ひとりごと（ランダムトーク）: 3〜7分おきに気まぐれに話す
  useEffect(() => {
    let timer: number;
    const schedule = () => {
      const delay = 180000 + Math.random() * 240000;
      timer = window.setTimeout(() => {
        // 設定オフ・トレイ格納中は黙ってスキップ（設定は実行時に最新値を読む）
        if (
          useSettingsStore.getState().mascotRandomTalk &&
          document.visibilityState === 'visible'
        ) {
          showMessageWithMouth(pickMascotTalk(), 5000);
        }
        schedule();
      }, delay);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [showMessageWithMouth]);

  // 空ちゃんをダブルクリックでパネル開閉
  useEffect(() => {
    const onDblClick = (e: MouseEvent) => {
      if (
        (e.target as Element | null)?.closest(
          '.mascot-bubble, .mascot-pills, .mascot-panel, .choice-box-overlay'
        )
      ) {
        return;
      }
      // ウィンドウ移動ドラッグ2連続などの誤発火を防ぐ
      if (document.body.dataset.mascotWindowDrag) return;
      const bounds = live2dModelRef.current?.getBounds();
      if (!bounds) return;
      const stage = document.querySelector('.mascot-stage');
      const offsetX = stage ? stage.getBoundingClientRect().left : 0;
      if (bounds.contains(e.clientX - offsetX, e.clientY)) {
        setPanelOpen((prev) => !prev);
      }
    };
    window.addEventListener('dblclick', onDblClick);
    return () => window.removeEventListener('dblclick', onDblClick);
  }, []);

  // メニュー（右クリック・トレイ）からのコマンドを受け取る
  useEffect(() => {
    const api = window.mascotApi;
    if (!api) return;

    return api.onCommand((command, payload) => {
      if (command === 'toggle-timer') {
        const { currentSession, startTimer, stopTimer } =
          usePomodoroStore.getState();
        if (currentSession.isRunning) {
          stopTimer();
        } else {
          startTimer();
        }
      } else if (command === 'toggle-panel') {
        setPanelOpen((prev) => !prev);
      } else if (
        command === 'set-panel-side' &&
        (payload === 'left' || payload === 'right')
      ) {
        useSettingsStore.getState().setMascotPanelSide(payload);
      } else if (command === 'select-dress' && typeof payload === 'string') {
        useDressStore.getState().selectDress(payload);
        showMessageWithMouth('着替えたよ！どうかな？', 4000);
      }
    });
  }, [showMessageWithMouth]);

  // メニューの表示状態（タイマー・着せ替え一覧）をmainプロセスへ同期
  useEffect(() => {
    const api = window.mascotApi;
    if (!api) return;

    api.updateMenuState({
      isRunning,
      panelSide: mascotPanelSide,
      dresses: dressList.map((d) => ({
        id: d.id,
        name: dressNames[d.nameKey]?.[language] ?? d.id,
        unlocked: dressUnlocks[d.id] || d.unlocked,
        selected: d.id === selectedDress,
      })),
    });
  }, [isRunning, selectedDress, dressUnlocks, language, mascotPanelSide]);

  return (
    <div
      className={`mascot-root ${
        mascotPanelSide === 'right' ? 'mascot-root--panel-right' : ''
      }`}
    >
      {/* サイドパネル（タイマー設定・統計・着せ替え・BGM） */}
      {panelOpen && (
        <MascotPanel
          bgmList={currentBgmList}
          isBgmPlaying={isBgmPlaying}
          currentBgmIndex={currentBgmIndex}
          onPlayBgm={playBgm}
          onStopBgm={stopBgm}
          onResetTimer={handleReset}
          onShowMessage={showMessageWithMouth}
          onDebugRestChoice={() =>
            showRestChoiceIfNeeded(showMessageWithMouth, showChoice, { force: true })
          }
        />
      )}

      {/* キャラクター側（常にウィンドウ右端に固定）
          移動: 体をつかんでドラッグ / パネル開閉: ダブルクリック / その他: 右クリックメニュー */}
      <div className="mascot-stage">
        <div className="avatar-area mascot-avatar-area">
          <Live2DCanvas
            modelPath={
              dressList.find((d) => d.id === selectedDress)?.modelPath ||
              dressList[0].modelPath
            }
            onModelLoad={(model) => {
              live2dModelRef.current = model;
              if (!greetedRef.current) {
                greetedRef.current = true;
                showMessageWithMouth('デスクトップに遊びに来たよ！', 4000);
              }
            }}
            onShowMessage={(message, duration) => {
              showMessageWithMouth(message, duration);
            }}
          />
        </div>

        <MascotBubble message={currentMessage} visible={messageVisible} />

        {/* 下部中央のピル: タイマーの右にBGMのレコード盤を並べる */}
        {(isBgmPlaying || phase) && (
          <div className="mascot-pills">
            {phase && (
              <div
                className={`mascot-timer ${
                  phase === 'break' ? 'mascot-timer--break' : ''
                }`}
              >
            <span className="mascot-timer__phase">
              {phase === 'break' ? 'BREAK' : 'WORK'}
            </span>
            <span className="mascot-timer__time">{formattedTime}</span>
            <button
              className="mascot-timer__btn"
              onClick={isRunning ? handleStop : handleStart}
              title={isRunning ? '一時停止' : '開始'}
            >
              {isRunning ? (
                <svg viewBox="0 0 12 12" fill="currentColor">
                  <rect x="2" y="1.5" width="3" height="9" rx="1" />
                  <rect x="7" y="1.5" width="3" height="9" rx="1" />
                </svg>
              ) : (
                <svg viewBox="0 0 12 12" fill="currentColor">
                  <path d="M3 1.8v8.4c0 .6.65.97 1.17.66l6.8-4.2a.78.78 0 0 0 0-1.32l-6.8-4.2A.78.78 0 0 0 3 1.8Z" />
                </svg>
              )}
            </button>
                <button
                  className="mascot-timer__btn"
                  onClick={handleReset}
                  title="停止"
                >
                  <svg viewBox="0 0 12 12" fill="currentColor">
                    <rect x="2.5" y="2.5" width="7" height="7" rx="1.5" />
                  </svg>
                </button>
              </div>
            )}

            {/* レコード盤: 再生中は回転、停止中は静止。クリックで再生/停止 */}
            <button
              className={`mascot-record ${
                isBgmPlaying ? 'mascot-record--spin' : ''
              }`}
              onClick={handleRecordClick}
              title={
                isBgmPlaying && currentBgmIndex !== null
                  ? `${currentBgmList[currentBgmIndex]?.name ?? 'BGM'} を停止`
                  : 'BGMを再生'
              }
            />
          </div>
        )}
      </div>

      {/* 選択肢システム（休息うながし等） */}
      <ChoiceBox
        visible={isChoiceVisible}
        question={currentScenario?.question}
        choices={currentScenario?.choices || []}
        onSelect={handleSelect}
        onTimeout={handleTimeout}
        timeoutSeconds={currentScenario?.timeoutSeconds}
      />
    </div>
  );
};

export default MascotPage;
